import { Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { Formation, FormationTheme, Player, Position, Team } from '../types';
import { DndContext } from '@dnd-kit/core';

import TeamPitchFormation from './TeamPitchFormation';
import { createRef, useEffect, useRef } from 'react';
import { CopyAll } from '@mui/icons-material';

type FormationViewProps = {
  teams: Team[];
  onFormationChange: (team: Team, formation: Formation) => void;
  onJerseyChange: (team: Team, theme: FormationTheme) => void;
  onSwitchPlayerPosition: (
    player: Player,
    fromTeam: Team,
    toTeam: Team,
    position?: Position
  ) => void;
  onRemovePlayer: (player: Player, team: Team) => void;
  onSetCustomTeamName?: (team: Team, customName: string) => void;
};

export default function FormationView({
  teams,
  onSwitchPlayerPosition,
  onFormationChange,
  onRemovePlayer,
  onJerseyChange,
  onSetCustomTeamName,
}: Readonly<FormationViewProps>) {
  const teamRefs = useRef([]);

  useEffect(() => {
    teamRefs.current = Array(teams.length)
      .fill(null)
      .map((_, i) => teamRefs.current[i] || createRef());
  }, [teams]);

  const handleDragEnd = (result: any) => {
    const droppableId = result.over?.id;
    if (!droppableId) {
      return;
    }

    const player = result.active.data.current.player as Player;
    if (!player) {
      return;
    }

    const sourceTeam = result.active.data.current.team as Team;
    if (droppableId.startsWith('trash')) {
      onRemovePlayer(player, sourceTeam);
      return;
    }

    let destTeam = result.over?.data?.current?.team as Team;
    if (!sourceTeam && !destTeam) {
      return;
    }
    if (!destTeam) {
      destTeam = sourceTeam;
    }

    let newPosition: Position | undefined;
    if (droppableId.startsWith('bench')) {
      newPosition = undefined;
    } else {
      newPosition = result.over.data.current.position as Position;
    }
    onSwitchPlayerPosition(player, sourceTeam, destTeam, newPosition);
  };

  const handleCopyAllImages = async () => {
    const images = await Promise.all(
      teamRefs.current.map(async (teamRef) => {
        return await (teamRef as any).current.copyImage();
      })
    );

    await navigator.clipboard.write(images);
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Stack alignItems="center">
        <Tooltip title="Copy all images" style={{ display: 'none' }}>
          <IconButton onClick={handleCopyAllImages}>
            <CopyAll color="primary" />
          </IconButton>
        </Tooltip>
        <Grid container spacing={4} style={{ marginTop: 20 }}>
          {teams.map((team, index) => (
            <Grid
              item
              key={team.name}
              xs={12}
              md={12}
              lg={6}
              style={{ marginBottom: 50 }}
            >
              <TeamPitchFormation
                ref={teamRefs.current[index]}
                onJerseyChange={onJerseyChange}
                onFormationChange={onFormationChange}
                onSetCustomTeamName={onSetCustomTeamName}
                team={team}
              />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </DndContext>
  );
}
